import React, { useState } from "react";

import { RcFile } from "antd/es/upload";
import moment from "moment";

import { parsePlatformContract, updatePlatformContractDescription } from "services";

import { handleS3Download, persistentQueryClient, useGetPlatformContractsAdmin } from "utils";

import { GET_PLATFORM_CONTRACTS_ADMIN, LANGUAGE_CODE, SUPPORTED_MARKETS } from "consts";

import { PlatformContract } from "models";

import {
  AdminHead,
  Button,
  Flex,
  Form,
  Icon,
  IconButton,
  MainPage,
  ModalContent,
  Option,
  Select,
  Tag,
  TextArea,
  Tooltip,
  Typography,
  Upload,
  Message as message,
} from "components";

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const handleUpload = async (file: RcFile, countryCode: string, languageCode: string, description: string | null = null) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    message({ content: `File exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB.`, icon: "error" });
    return false;
  }

  await parsePlatformContract({
    file,
    countryCode,
    languageCode,
    description,
  });

  return false;
};

const handlePublishDescription = async (activeContract: PlatformContract, description: string, setIsPublishing: (isPublishing: boolean) => void) => {
  if (!activeContract) return;
  setIsPublishing(true);
  try {
    await updatePlatformContractDescription({
      contractId: activeContract.id,
      description: description.trim() || null,
    });
    persistentQueryClient.invalidateQueries({ queryKey: [GET_PLATFORM_CONTRACTS_ADMIN] });
  } finally {
    setIsPublishing(false);
  }
};

const ContractRow = ({ contract }: { contract: PlatformContract }) => {
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  return (
    <>
      <Flex align="center" justify="space-between">
        <div>
          {!contract.replacedById && (
            <>
              <Tag colour="green" text="Active" />
              <br />
            </>
          )}
          <span className={contract.replacedById ? "grey-text" : ""}>ID: {contract.id}</span>
          <br />
          <span className={contract.replacedById ? "grey-text" : ""}>Language: {contract.languageCode.toLowerCase()}</span>
          <br />
          <span className={contract.replacedById ? "grey-text" : ""}>Uploaded at: {moment(contract.createdAt).format("DD.MM.YYYY")}</span>
        </div>
        <Flex align="center">
          <Tooltip title="View description">
            <Icon iconName="tabler/list" onClick={() => setDescriptionModalOpen(true)} className="pointer" />
          </Tooltip>
          <Tooltip title="Download contract">
            <Icon className="margin-left-100 pointer" iconName="tabler/download" onClick={() => handleS3Download(contract.link)} />
          </Tooltip>
          {!contract.replacedById && (
            <Upload
              name="file"
              showUploadList={false}
              action=""
              beforeUpload={(v) => handleUpload(v, contract.countryCode, contract.languageCode)}
              className="auto-height contract-upload small"
            >
              <Tooltip color="#db3804" title="Replace active contract">
                <IconButton iconName="tabler/replace" />
              </Tooltip>
            </Upload>
          )}
        </Flex>
      </Flex>
      <ModalContent visible={descriptionModalOpen} onCancel={() => setDescriptionModalOpen(false)}>
        <Typography variant="h3" className="margin-bottom-200">
          Description
        </Typography>
        <Typography variant="paragraph-m" style={{ whiteSpace: "pre-line" }}>
          {contract.description || "No description set."}
        </Typography>
      </ModalContent>
      <hr className="margin-bottom-100" />
    </>
  );
};

const DescriptionForm = ({ activeContract, countryCode }: { activeContract: PlatformContract; countryCode: string }) => {
  const [description, setDescription] = useState<string>(activeContract?.description || "");
  const [isPublishing, setIsPublishing] = useState(false);

  return (
    <Form>
      <Form.Item className="margin-bottom-200" name="description">
        <Flex vertical align="center" gap="0.25rem">
          <TextArea
            name="description"
            initialValue={description}
            onChange={(e) => setDescription(e.target.value)}
            className="margin-bottom-100 active width-100"
            label="Notification shown to enrolled farmers, summarising the changes of the latest contract."
            autoSize={{ minRows: 4 }}
          />
          <Button
            type="primary"
            disabled={description.trim() === activeContract?.description?.trim() || isPublishing}
            loading={isPublishing}
            onClick={() => handlePublishDescription(activeContract, description, setIsPublishing)}
          >{`Publish description for active ${countryCode} contract`}</Button>
        </Flex>
      </Form.Item>
    </Form>
  );
};

const Market = ({ platformContracts, countryCode }: { platformContracts?: PlatformContract[]; countryCode: string }) => {
  const existingContracts = platformContracts?.filter((contract) => contract.countryCode === countryCode) ?? [];
  const activeContract = existingContracts.find((contract) => !contract.replacedById);

  return (
    <div className="margin-bottom-400" key={countryCode}>
      <Typography variant="h3" className="margin-bottom-200">{`Platform contracts: ${countryCode}`}</Typography>
      {activeContract && <DescriptionForm activeContract={activeContract} countryCode={countryCode} />}
      <div className="margin-bottom-200">{existingContracts.map((contract) => <ContractRow contract={contract} key={contract.id} />) ?? null}</div>
      {!existingContracts?.length && <Typography variant="paragraph-m">No contract uploaded"</Typography>}
    </div>
  );
};

export const PlatformTerms = () => {
  const { data: platformContracts, isLoading } = useGetPlatformContractsAdmin();

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");

  const defaultLanguages: Record<string, string> = {
    DE: LANGUAGE_CODE.GERMAN,
    PL: LANGUAGE_CODE.POLISH,
    GB: LANGUAGE_CODE.ENGLISH_GB,
    US: LANGUAGE_CODE.ENGLISH_US,
    CZ: LANGUAGE_CODE.CZECH,
    SK: LANGUAGE_CODE.SLOVAK,
    HU: LANGUAGE_CODE.HUNGARIAN,
  };

  const existingMarkets = platformContracts?.reduce<string[]>((acc, { countryCode }) => (acc.includes(countryCode) ? acc : [...acc, countryCode]), []) ?? [];
  const newMarkets = Object.values(SUPPORTED_MARKETS).filter((market) => !platformContracts?.find((contract) => contract.countryCode === market));

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <AdminHead
        headline="Update platform terms"
        description={
          <Flex vertical>
            <Typography variant="paragraph-m" color="grey-600">
              Replacing an existing contract will mean that all farmers who have signed that contract will see a popup where they are shown the new contract.
            </Typography>
            <Typography variant="paragraph-m" color="grey-600">
              The maximum file size is {MAX_FILE_SIZE_MB} MB.
            </Typography>
          </Flex>
        }
      />
      <section className="container tiny" style={{ maxWidth: "768px" }}>
        {existingMarkets.map((countryCode) => (
          <Market key={countryCode} platformContracts={platformContracts} countryCode={countryCode} />
        ))}
        <Typography variant="h3" className="margin-bottom-200">
          Add terms & conditions for a new market
        </Typography>
        <Flex align="center">
          <Select
            label="Select a new market"
            rules={[{ required: false }]}
            value={selectedCountryCode}
            onChange={setSelectedCountryCode}
            style={{ width: "150px" }}
          >
            {newMarkets.map((market) => (
              <Option key={market} value={market}>
                {market}
              </Option>
            ))}
          </Select>
          <Upload
            disabled={!selectedCountryCode}
            name="file"
            showUploadList={false}
            beforeUpload={(v) => handleUpload(v, selectedCountryCode, defaultLanguages[selectedCountryCode])}
            className="auto-height contract-upload small"
          >
            <Tooltip color="#db3804" title="Upload terms for new market">
              <IconButton iconName="tabler/upload" disabled={!selectedCountryCode} />
            </Tooltip>
          </Upload>
        </Flex>
      </section>
    </MainPage>
  );
};
